import {ApiClient, HttpMethod, RequestOptions} from "../../appfw/api-client/ApiClient.ts";
import {User} from "../../appfw/api-client/AppFwApiClient.ts";

export interface DiscoverResponse {
    readonly accounts: Account[];
}

export type AccountMemberRole = "owner"

export interface Account {
    readonly id: string;
    readonly podName: string;
    readonly role?: AccountMemberRole;
}

export interface Member {
    readonly user: User;
    readonly email?: string;
    readonly role?: "owner"
}

export interface Invitation {
    readonly id: string,
    readonly invitedDate: string,
    readonly inviter: User,
    readonly email: string,
    readonly podName: string,
}

export interface GooglePlace {
    readonly id: string;
    readonly name: string;
    readonly address: string;
}

export interface IntegrationSyncInfo {
    readonly is_syncing: boolean;
    readonly last_sync_finished_at?: string;
}

export interface GCalIntegration {
    readonly id: string
    readonly google_calendar_id: string
    readonly google_calendar_hash: string
    readonly google_calendar_name: string
    readonly default_google_place_id?: string
    readonly trusted: boolean
    readonly sync_info: IntegrationSyncInfo
}

export interface EmailContacts {
    readonly emailContacts: EmailContact[]
}

export interface EmailContact {
    readonly email: string
    readonly name?: string
}

export interface Email {
    readonly to: EmailContact[];
    readonly subject: string;
    readonly message: string;
}

export interface InfoEmailRequest {
    readonly emailAddresses: string[];
    readonly startDateMin?: Date;
    readonly startDateMax?: Date;
}

export interface UpdateEmailRequest {
    readonly emailAddresses: string[];
    readonly dateModifiedMin?: Date;
}

class EventerConsoleApiClient extends ApiClient {

    constructor() {
        super(process.env.NODE_ENV === 'production' ? 'https://api.eventer.app/v1/console/' : 'http://localhost:8888/v1/console/')
    }

    async discover() {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<DiscoverResponse>(
            `accounts/discover`,
            {
                sessionId: sessionId,
                method: HttpMethod.GET,
            },
        )
    }

    async createAccount(params: { podName: string; }) {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Account>(
            `accounts`,
            {
                sessionId: sessionId,
                params: {
                    podName: params.podName,
                }
            },
        )
    }

    async deleteAccount(params: {
        accountId: string;
    }) {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<undefined>(
            `accounts/${params.accountId}`,
            {
                method: HttpMethod.DELETE,
                sessionId: sessionId,
            },
        )
    }

    async listMembers(params: {
        accountId: string;
    }, opts?: RequestOptions): Promise<Member[]> {
        return this.get(`accounts/${params.accountId}/members`, opts)
    }

    async removeMember(params: {
        accountId: string;
        memberId: string;
    }, opts?: RequestOptions): Promise<undefined> {
        return this.delete(`accounts/${params.accountId}/members/${params.memberId}`, opts)
    }

    async listInvitations(params: {
        accountId: string;
    }, opts?: RequestOptions): Promise<Invitation[]> {
        return this.get(`accounts/${params.accountId}/invitations`, opts)
    }

    async deleteInvitation(params: {
        accountId: string;
        invitationId: string;
    }, opts?: RequestOptions): Promise<undefined> {
        return this.delete(`accounts/${params.accountId}/invitations/${params.invitationId}`, opts)
    }

    async inviteMember(params: {
        accountId: string;
        email: string;
    }): Promise<Invitation> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<Invitation>(
            `accounts/${params.accountId}/invitations`,
            {
                method: HttpMethod.POST,
                sessionId: sessionId,
                params: {
                    email: params.email,
                }
            },
        )
    }

    async getInvitation(params: {
        accountId: string;
        invitationId: string;
    }, opts?: RequestOptions): Promise<Invitation> {
        return this.get(`accounts/${params.accountId}/invitations/${params.invitationId}`, opts)
    }

    async acceptInvitation(params: {
        accountId: string;
        invitationId: string;
    }, opts?: RequestOptions): Promise<Invitation> {
        return this.post(`accounts/${params.accountId}/invitations/${params.invitationId}/accept`, null, opts)
    }

    async searchPlaces(
        params: { q: string; }
    ): Promise<GooglePlace[]> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<GooglePlace[]>(
            `places/search?q=${params.q}`,
            {
                sessionId: sessionId,
                method: HttpMethod.GET,
            },
        )
    }

    async testGCalIntegration(params: {
        accountId: string;
        gCalId: string;
        defaultGooglePlaceId: string;
    }) {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<undefined>(
            `accounts/${params.accountId}/integrations/gcal/test`,
            {
                sessionId: sessionId,
                params: {
                    "google_calendar_id": params.gCalId,
                    "default_google_place_id": params.defaultGooglePlaceId,
                },
            },
        )
    }

    async getGCalIntegrations(params: {
        accountId: string;
    }): Promise<GCalIntegration[]> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<GCalIntegration[]>(
            `accounts/${params.accountId}/integrations/gcal`,
            {
                sessionId: sessionId,
                method: HttpMethod.GET,
            },
        )
    }

    async getGCalIntegration(params: {
        accountId: string;
        id: string;
    }): Promise<null | GCalIntegration> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<GCalIntegration>(
            `accounts/${params.accountId}/integrations/gcal/${params.id}`,
            {
                sessionId: sessionId,
                method: HttpMethod.GET,
            },
        )
    }

    async addGCalIntegration(params: {
        accountId: string;
        gCalId: string;
        defaultGooglePlaceId: string;
    }): Promise<GCalIntegration> {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<GCalIntegration>(
            `accounts/${params.accountId}/integrations/gcal`,
            {
                sessionId: sessionId,
                params: {
                    "google_calendar_id": params.gCalId,
                    "default_google_place_id": params.defaultGooglePlaceId,
                },
            },
        )
    }

    async syncGCalIntegration(params: {
        accountId: string;
        gCalId: string;
    }) {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<GCalIntegration>(
            `accounts/${params.accountId}/integrations/gcal/${params.gCalId}/sync`,
            {
                sessionId: sessionId,
            },
        )
    }

    async deleteGCalIntegration(params: {
        accountId: string;
        gCalId: string;
    }) {
        let sessionId = localStorage.getItem('sessionId')
        return this.fetch<GCalIntegration>(
            `accounts/${params.accountId}/integrations/gcal/${params.gCalId}`,
            {
                method: HttpMethod.DELETE,
                sessionId: sessionId,
            },
        )
    }

    async getEmailContacts(params: {
        accountId: string;
    }): Promise<EmailContacts> {
        return this.get(`accounts/${params.accountId}/services/emails/contacts`)
    }

    async addEmailContacts(params: {
        accountId: string;
        emailContacts: EmailContacts;
    }) {
        return this.post(`accounts/${params.accountId}/services/emails/contacts/add`, params.emailContacts)
    }

    async removeEmailContacts(params: {
        accountId: string;
        emailAddresses: String[];
    }) {
        return this.post(`accounts/${params.accountId}/services/emails/contacts/remove`, {
            emailAddresses: params.emailAddresses,
        })
    }

    async generateInfoEmail(
        params: {
            accountId: string,
            payload: InfoEmailRequest,
        }
    ) {
        return this.post<Email>(`accounts/${params.accountId}/services/emails/info`, params.payload)
    }

    async sendInfoEmail(
        params: {
            accountId: string,
            payload: InfoEmailRequest,
        }
    ) {
        return this.post<Email>(`accounts/${params.accountId}/services/emails/info/send`, params.payload)
    }

    async generateUpdateEmail(
        params: {
            accountId: string,
            payload: UpdateEmailRequest,
        }
    ) {
        return this.post<Email>(`accounts/${params.accountId}/services/emails/update`, params.payload)
    }

    async sendUpdateEmail(
        params: {
            accountId: string,
            payload: UpdateEmailRequest,
        }
    ) {
        return this.post<Email>(`accounts/${params.accountId}/services/emails/update/send`, params.payload)
    }

    // async testInfoEmail(params: {
    //     accountId: string;
    //     gCalIntegrationId: string;
    //     infoEmailRequest: InfoEmailRequest;
    // }) {
    //     let sessionId = localStorage.getItem('sessionId')
    //     return this.fetch<Email>(
    //         `accounts/${params.accountId}/integrations/gcal/${params.gCalIntegrationId}/mails/test-info-email`,
    //         {
    //             sessionId: sessionId,
    //             params: params.infoEmailRequest,
    //         },
    //     )
    // }
    //
    // async sendInfoEmail(params: {
    //     accountId: string;
    //     gCalIntegrationId: string;
    //     infoEmailRequest: InfoEmailRequest;
    // }) {
    //     let sessionId = localStorage.getItem('sessionId')
    //     return this.fetch<undefined>(
    //         `accounts/${params.accountId}/integrations/gcal/${params.gCalIntegrationId}/mails/send-info-email`,
    //         {
    //             sessionId: sessionId,
    //             params: params.infoEmailRequest,
    //         },
    //     )
    // }
}


const eventerConsoleApiClient = new EventerConsoleApiClient();
export {eventerConsoleApiClient};
